import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { createPortal } from 'react-dom'
import { Mail, Phone } from 'lucide-react'
import { useTopLayerPortal } from '../utils/top-layer-portal'

interface LeaveDetailsPopupProps {
  primaryColor: string
  onSubmit: (email: string, phone: string) => void
  onDismiss: () => void
}

/**
 * LeaveDetailsPopup — Post-session mini form for deferred contact mode.
 * Shown when session ends without contact info.
 * "רוצה שנחזור אליך? השאר פרטים"
 */
export const LeaveDetailsPopup: React.FC<LeaveDetailsPopupProps> = ({ primaryColor, onSubmit, onDismiss }) => {
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [emailError, setEmailError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const portalTarget = useTopLayerPortal()

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const phoneRegex = /^[0-9]{9,15}$/

  const validateEmail = (value: string): boolean => {
    if (!value) { setEmailError('נא למלא אימייל'); return false }
    if (!emailRegex.test(value)) { setEmailError('נא למלא אימייל תקין'); return false }
    setEmailError('')
    return true
  }

  const validatePhone = (value: string): boolean => {
    const clean = value.replace(/[\s\-()]/g, '')
    if (!clean) { setPhoneError('נא למלא מספר טלפון'); return false }
    if (!phoneRegex.test(clean)) { setPhoneError('נא למלא מספר טלפון תקין (9-15 ספרות)'); return false }
    setPhoneError('')
    return true
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const isEmailValid = validateEmail(email)
    const cleanPhone = phone.replace(/[\s\-()]/g, '')
    const isPhoneValid = validatePhone(cleanPhone)
    if (isEmailValid && isPhoneValid) {
      onSubmit(email, cleanPhone)
    }
  }

  const isFormValid = email && phone && !emailError && !phoneError

  // Fall back to inline rendering if the portal target is unavailable, so
  // the form is never silently invisible.
  const overlay = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))',
        boxSizing: 'border-box',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        pointerEvents: 'auto',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        style={{
          maxWidth: 380,
          width: '100%',
          maxHeight: 'min(560px, calc(100dvh - 32px - env(safe-area-inset-top) - env(safe-area-inset-bottom)))',
          backgroundColor: 'white',
          borderRadius: 16,
          padding: 28,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          boxSizing: 'border-box',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          direction: 'rtl',
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📞</div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
            רוצה שנחזור אליך?
          </h3>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#6b7280' }}>
            השאר פרטים ונחזור אליך בהקדם
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ position: 'relative' }}>
              <Mail size={16} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (emailError) validateEmail(e.target.value) }}
                onBlur={() => email && validateEmail(email)}
                placeholder="אימייל"
                dir="ltr"
                style={{
                  width: '100%',
                  padding: '12px 40px 12px 12px',
                  borderRadius: 8,
                  border: `2px solid ${emailError ? '#ef4444' : '#e5e7eb'}`,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  backgroundColor: '#ffffff',
                  color: '#111827',
                  WebkitTextFillColor: '#111827',
                  caretColor: '#111827',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => { if (!emailError) e.currentTarget.style.borderColor = primaryColor }}
                onBlurCapture={(e) => { if (!emailError) e.currentTarget.style.borderColor = '#e5e7eb' }}
              />
            </div>
            {emailError && <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0' }}>{emailError}</p>}
          </div>

          {/* Phone */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ position: 'relative' }}>
              <Phone size={16} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="tel"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); if (phoneError) validatePhone(e.target.value) }}
                onBlur={() => phone && validatePhone(phone)}
                placeholder="טלפון"
                dir="ltr"
                style={{
                  width: '100%',
                  padding: '12px 40px 12px 12px',
                  borderRadius: 8,
                  border: `2px solid ${phoneError ? '#ef4444' : '#e5e7eb'}`,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  backgroundColor: '#ffffff',
                  color: '#111827',
                  WebkitTextFillColor: '#111827',
                  caretColor: '#111827',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => { if (!phoneError) e.currentTarget.style.borderColor = primaryColor }}
                onBlurCapture={(e) => { if (!phoneError) e.currentTarget.style.borderColor = '#e5e7eb' }}
              />
            </div>
            {phoneError && <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0' }}>{phoneError}</p>}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="submit"
              disabled={!isFormValid}
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: 8,
                border: 'none',
                background: isFormValid
                  ? `linear-gradient(135deg, ${primaryColor}, ${adjustBrightness(primaryColor, 0.8)})`
                  : '#e5e7eb',
                color: isFormValid ? 'white' : '#9ca3af',
                fontSize: 15,
                fontWeight: 600,
                cursor: isFormValid ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                opacity: isFormValid ? 1 : 0.6,
              }}
            >
              שלח
            </button>
            <button
              type="button"
              onClick={onDismiss}
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: 8,
                border: '2px solid #e5e7eb',
                background: 'white',
                color: '#6b7280',
                fontSize: 15,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb' }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white' }}
            >
              לא תודה
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )

  return portalTarget ? createPortal(overlay, portalTarget) : overlay
}

function adjustBrightness(color: string, factor: number): string {
  const hex = color.replace('#', '')
  const r = Math.round(parseInt(hex.substring(0, 2), 16) * factor)
  const g = Math.round(parseInt(hex.substring(2, 4), 16) * factor)
  const b = Math.round(parseInt(hex.substring(4, 6), 16) * factor)
  return '#' + [r, g, b].map(v => Math.min(255, v).toString(16).padStart(2, '0')).join('')
}
